import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface FilterBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Bound value for the search input. Omit to hide the search field. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Left-aligned filter controls (chips, selects, popovers). */
  filters?: React.ReactNode;
  /** Right-aligned actions (e.g. Add / Export). */
  actions?: React.ReactNode;
  /** When provided, renders a "Clear" button that calls this handler. */
  onClear?: () => void;
  /** Show the clear button — usually `true` when any filter is dirty. */
  hasActiveFilters?: boolean;
}

/**
 * FilterBar — horizontal cluster of search + filter controls + actions.
 *
 * The kit owns the layout (responsive flex, alignment, spacing); the
 * consumer supplies the actual filter widgets. Pair with TableShell or
 * DashboardCard's `headerActions` slot.
 */
export const FilterBar = React.forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      searchValue,
      onSearchChange,
      searchPlaceholder = 'Search…',
      filters,
      actions,
      onClear,
      hasActiveFilters,
      className,
      ...props
    },
    ref,
  ) => {
    const showSearch = typeof searchValue !== 'undefined' || !!onSearchChange;
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col gap-2.5 md:flex-row md:items-center md:justify-between',
          className,
        )}
        {...props}
      >
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {showSearch && (
            <div className="relative w-full md:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchValue ?? ''}
                onChange={(event) => onSearchChange?.(event.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8"
              />
            </div>
          )}
          {filters}
          {hasActiveFilters && onClear && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    );
  },
);
FilterBar.displayName = 'FilterBar';
